// Local portfolio data for Keshav Mishra (replaces DatoCMS content)
import { ProfileBanner, WorkPermit, TimelineItem, Project, Certification, ContactMe, Skill } from '../types';

export const profileBanner: ProfileBanner = {
  backgroundImage: { url: 'https://picsum.photos/seed/keshav-banner/1920/1080' },
  headline: 'Software Engineer (AI/ML) | Building and Shipping Real Systems',
  resumeLink: { url: 'https://drive.google.com/file/d/1elhQ6MB9k0bt0MUfhg9NsFxwDTSFG9Zb/view?usp=sharing' },
  linkedinLink: 'https://www.linkedin.com/in/MKeshav04/',
  profileSummary:
    "B.Tech IT 2026 graduate focused on building and shipping real systems. I've deployed full-stack applications, end-to-end data pipelines processing 90K+ records, and ML models in production. I work across the stack, Python, FastAPI, React, SQL, and have hands-on experience integrating LLM APIs and cloud infrastructure. Outside of tech, I've spent years managing international operations and stakeholder relationships across global organizations, which means I know how to execute, communicate, and figure things out without a playbook.",
};

export const workPermit: WorkPermit = {
  visaStatus: '',
  expiryDate: new Date(),
  summary: '',
  additionalInfo: '',
};

export const timeline: TimelineItem[] = [
  {
    timelineType: 'work',
    name: 'Legacore Infomatics',
    title: 'Python AI/ML Intern',
    techStack: 'Python, RAG, ChromaDB, sentence-transformers, spaCy, Groq LLM APIs, fuzzy matching, regex',
    summaryPoints: [
      'Building the production fact-extraction and summarization pipeline that turns personal-injury case records into structured medical summaries and chronologies, with an accuracy-first approach',
      'Developing the retrieval and NLP toolkit: RAG search over medical datasets (hierarchical chunking, sentence-transformers embeddings, ChromaDB), spaCy entity extraction, fuzzy matching, and regex',
      'Using Groq-based tool calling, choosing each method under a Cost/Speed/Accuracy framework',
    ],
    dateRange: 'Aug 2026 – Present',
  },
  {
    timelineType: 'work',
    name: 'Esports Operations & Business Development',
    title: 'Leadership & International Operations',
    techStack: 'Stakeholder management, performance analytics, sponsorship acquisition, cross-regional ops (APAC, EMEA, NA)',
    summaryPoints: [
      'Managed international esports operations across APAC, EMEA, and NA markets throughout college, working across multiple organizations simultaneously with cross-regional teams and external stakeholders',
      "Led EWC (Esports World Cup) operations at Godlike Esports, one of the world's largest esports tournaments with a $70M+ prize pool, handling end-to-end coordination across talent scouting, logistics, and stakeholder communication across Saudi Arabia and Paris",
      'Negotiated and managed sponsorship deals worth $40,000+ monthly, working directly with brand stakeholders, legal teams, and organizational leadership',
      'Coordinated 20+ competitive teams across 16+ titles, managing performance analysis, strategic planning, and cross-functional execution under high-pressure, time-sensitive environments. Each team consisted of 3–6 players.',
      'Built and maintained professional relationships with stakeholders across international organizations, developing communication, negotiation, and execution skills in a fast-paced, results-driven industry',
    ],
    dateRange: '2020 – Present',
  },
  {
    timelineType: 'education',
    name: 'AKTU',
    title: 'Bachelor of Technology (B.Tech) — Information Technology',
    techStack: '',
    summaryPoints: [
      'Relevant coursework: Database Management Systems, Data Structures & Algorithms, Cloud Computing, Statistics, Machine Learning, Software Engineering, Operating Systems, Computer Networks.',
      'Expected graduation: May 2026.',
    ],
    dateRange: '2022 – 2026',
  },
  {
    timelineType: 'education',
    name: 'Sheth Vidya Mandir, Mumbai',
    title: 'Higher Secondary (XII) — Science',
    techStack: '',
    summaryPoints: ['Completed higher secondary education in Science.'],
    dateRange: '2020 – 2022',
  },
];

export const projects: Project[] = [
  {
    title: 'SafeTools',
    description:
      'Privacy-first toolkit for PDF, image and video tasks. Light tools run fully in the browser via WebAssembly and Web Workers; heavy ones run on a Dockerized FastAPI backend with Ghostscript, LibreOffice, yt-dlp and FFmpeg.',
    techUsed: 'React, Vite, FastAPI, Docker, WebAssembly, FFmpeg, Netlify, Render',
    image: { url: '/images/projects/safetools.png' },
    liveUrl: 'https://safetools.online/',
  },
  {
    title: 'Kharcha — Expense Track-kar',
    description:
      'Mobile app (installable PWA): a personal expense track-kar built for myself and a couple of friends. Type "150 food" and it is logged by a custom parser that learns from corrections. Monthly budget with what is left to spend, a Plan tab for upcoming bills and must-buys, and savings goals. Works offline, keeps all data on the phone, and Groq AI writes short spending summaries.',
    techUsed: 'React, Vite, PWA, IndexedDB, Groq API, Netlify',
    image: { url: '/images/projects/kharcha.png' },
    liveUrl: 'https://expense-track-kar.netlify.app/',
    githubUrl: 'https://github.com/MKeshav04/expense-track-kar',
  },
  {
    title: 'FlashForge',
    description:
      'AI flashcard system with Groq LLM pipeline, SM-2 spaced repetition, async FastAPI, JWT auth, PostgreSQL/Supabase with RLS, and Docker.',
    techUsed: 'Python, FastAPI, React, Groq API, Docker, Supabase, PostgreSQL, JWT, Netlify, Hugging Face Spaces',
    image: { url: '/images/projects/flashforge.png' },
    liveUrl: 'https://flash-forge.netlify.app/',
    githubUrl: 'https://github.com/MKeshav04/FlashForge-AI-POWERED-FLASHCARDS-WITH-SM-2',
  },
  {
    title: 'Tech Talent Market Analysis',
    description:
      'ETL pipeline on 90K+ survey responses with 35%+ missing-value handling; predictive models with PyTorch and TensorFlow on 80+ features; Streamlit dashboard for exploration.',
    techUsed: 'Python, Pandas, NumPy, scikit-learn, PyTorch, TensorFlow, Streamlit, ETL Pipelines',
    image: { url: '/images/projects/tech-talent.png' },
    liveUrl: 'https://predict-salaryy.streamlit.app/',
    githubUrl: 'https://github.com/MKeshav04/Tech-Talent-Market-Analysis',
  },
  {
    title: 'Netflix-Style Portfolio',
    description:
      "This site: a Netflix-inspired personal portfolio with profile-based content (\"Who's Watching?\"), custom branding, SEO and link-preview tags, deployed on Netlify.",
    techUsed: 'React, TypeScript, React Router, Netlify',
    image: { url: '/images/projects/portfolio.png' },
    githubUrl: 'https://github.com/MKeshav04/Netflix-Portfolio',
  },
  {
    title: 'Infinite Tic-Tac-Toe',
    description:
      'Full-stack app with cyclic 3-move FIFO memory constraint and Minimax AI across Player vs Player and Player vs AI modes. End-to-end SDLC with Git/GitHub and split cloud deployment.',
    techUsed: 'Python, FastAPI, React, Uvicorn, REST APIs, GitHub, Vercel, Render',
    image: { url: '/images/projects/infinite-tic-tac-toe.png' },
    liveUrl: 'https://infinite-tic-tac-toe-six.vercel.app/',
    githubUrl: 'https://github.com/MKeshav04/infinite-tic-tac-toe',
    note: 'The backend is currently offline, so the live game will not respond. The code is on GitHub.',
  },
  {
    title: 'Night Storm Demoscene',
    description:
      'A rainy night thunderstorm in a single 57 KB HTML file. Click, clap or shout and lightning cracks across the clouds, and the thunder arrives seconds later, just like a real storm. Every pixel and every sound is generated by code.',
    techUsed: 'WebGL2, GLSL, JavaScript, Web Audio API, Canvas 2D, Web Workers, GitHub Pages',
    image: { url: '/images/projects/night-storm.png' },
    liveUrl: 'https://mkeshav04.github.io/night-storm-demoscene/',
    githubUrl: 'https://github.com/MKeshav04/night-storm-demoscene',
  },
];

export const certifications: Certification[] = [
  {
    title: 'AWS Certified Cloud Practitioner',
    issuer: 'Amazon',
    issuedDate: '',
    iconName: 'aws',
  },
  {
    title: 'OCI Data Science',
    issuer: 'Oracle',
    issuedDate: '',
    iconName: 'oracle',
  },
  {
    title: 'OCI Generative AI',
    issuer: 'Oracle',
    issuedDate: '',
    iconName: 'oracle',
  },
  {
    title: 'Supervised Machine Learning: Regression and Classification',
    issuer: 'Coursera (DeepLearning.AI / Stanford University)',
    issuedDate: '',
    iconName: 'coursera',
  },
  {
    title: 'Advanced Learning Algorithms',
    issuer: 'Coursera (DeepLearning.AI / Stanford University)',
    issuedDate: '',
    iconName: 'coursera',
  },
];

export const contactMe: ContactMe = {
  profilePicture: { url: '/images/profile.jpg' },
  name: 'Keshav Mishra',
  title: 'Software Engineer (AI/ML) | Building and Shipping Real Systems',
  summary:
    '2026 B.Tech IT graduate, currently a Python AI/ML Intern at Legacore Infomatics, building document-extraction and RAG pipelines for legal and medical teams. I build and ship across Python, FastAPI, React, and SQL.',
  companyUniversity: "Legacore Infomatics · AKTU '26",
  linkedinLink: 'https://www.linkedin.com/in/MKeshav04/',
  email: 'Keshav04mishra@gmail.com',
  phoneNumber: '+91 9175008882',
};

const skill = (name: string, category: string, description: string, icon: string): Skill => ({
  name,
  category,
  description,
  icon,
});

export const skills: Skill[] = [
  skill('Python', 'Programming Languages', 'Primary language for all projects', 'FaPython'),
  skill('C++', 'Programming Languages', 'Data structures and algorithms', 'FaCode'),
  skill('JavaScript', 'Programming Languages', 'Frontend and API integration', 'FaJs'),
  skill('SQL', 'Programming Languages', 'Database design and complex queries', 'FaDatabase'),
  skill('HTML', 'Programming Languages', 'Web development', 'SiHtml5'),
  skill('CSS / SCSS', 'Programming Languages', 'Styling and responsive design', 'SiCss3'),
  skill('Groq API', 'AI & Machine Learning', 'LLM integration and deployment', 'FaBrain'),
  skill('Prompt Engineering', 'AI & Machine Learning', 'Production LLM pipelines', 'FaBrain'),
  skill('scikit-learn', 'AI & Machine Learning', 'Predictive modeling workflows', 'FaPython'),
  skill('PyTorch', 'AI & Machine Learning', 'Deep learning model development and training', 'SiPytorch'),
  skill('TensorFlow', 'AI & Machine Learning', 'Neural network workflows and model deployment', 'SiTensorflow'),
  skill('RAG', 'AI & Machine Learning', 'Retrieval over documents with chunking and embeddings', 'FaBrain'),
  skill('ChromaDB', 'AI & Machine Learning', 'Vector search with metadata filtering', 'FaDatabase'),
  skill('sentence-transformers', 'AI & Machine Learning', 'Text embeddings for semantic search', 'FaBrain'),
  skill('spaCy', 'AI & Machine Learning', 'NER, matchers and NLP pipelines', 'SiSpacy'),
  skill('Fuzzy Matching / Regex', 'AI & Machine Learning', 'Text similarity and pattern extraction', 'FaCode'),
  skill('XGBoost / Random Forest', 'AI & Machine Learning', 'Ensemble models for tabular data', 'FaPython'),
  skill('Feature Engineering', 'AI & Machine Learning', 'Data transformation for ML', 'FaPython'),
  skill('PostgreSQL', 'Databases & Data Management', 'Production DB with RLS', 'SiPostgresql'),
  skill('MySQL', 'Databases & Data Management', 'Relational database management', 'SiMysql'),
  skill('Oracle', 'Databases & Data Management', 'Enterprise database systems', 'FaDatabase'),
  skill('MongoDB', 'Databases & Data Management', 'NoSQL operations', 'SiMongodb'),
  skill('Supabase', 'Databases & Data Management', 'BaaS with real-time features', 'SiSupabase'),
  skill('FastAPI', 'Frameworks & Backend', 'Async API development', 'FaPython'),
  skill('React.js', 'Frameworks & Backend', 'Component-based frontend', 'FaReact'),
  skill('React / Vite', 'Frameworks & Backend', 'Modern frontend tooling', 'FaReact'),
  skill('Flask', 'Frameworks & Backend', 'Lightweight Python web apps', 'SiFlask'),
  skill('SQLAlchemy', 'Frameworks & Backend', 'Python ORM for SQL databases', 'SiSqlalchemy'),
  skill('Docker', 'Frameworks & Backend', 'Containerization and deployment', 'FaDocker'),
  skill('REST APIs', 'Frameworks & Backend', 'API design and integration', 'FaCode'),
  skill('Uvicorn', 'Frameworks & Backend', 'ASGI server deployment', 'FaPython'),
  skill('AWS', 'Cloud & Deployment', 'Cloud Practitioner certified', 'FaAws'),
  skill('Vercel', 'Cloud & Deployment', 'Frontend deployment', 'SiVercel'),
  skill('WebAssembly', 'Cloud & Deployment', 'In-browser processing with Web Workers', 'SiWebassembly'),
  skill('Netlify', 'Cloud & Deployment', 'Static site deployment', 'SiNetlify'),
  skill('Render', 'Cloud & Deployment', 'Backend API hosting', 'SiRender'),
  skill('Railway', 'Cloud & Deployment', 'Application deployment', 'FaCloud'),
  skill('Hugging Face Spaces', 'Cloud & Deployment', 'ML model deployment', 'FaBrain'),
  skill('Pandas', 'Data Science & Visualization', 'Data manipulation and analysis', 'FaPython'),
  skill('NumPy', 'Data Science & Visualization', 'Numerical computing', 'FaPython'),
  skill('ETL Pipelines', 'Data Science & Visualization', 'Extraction, transformation, loading', 'FaPython'),
  skill('Plotly', 'Data Science & Visualization', 'Interactive visualizations', 'FaChartLine'),
  skill('Streamlit', 'Data Science & Visualization', 'Interactive data apps', 'FaPython'),
  skill('Power BI', 'Data Science & Visualization', 'Business intelligence dashboards', 'FaChartLine'),
  skill('Excel', 'Data Science & Visualization', 'Data analysis and reporting', 'FaFileExcel'),
  skill('Git / GitHub', 'Development Tools & Practices', 'Version control and collaboration', 'FaGithub'),
  skill('Claude Code / Cursor', 'Development Tools & Practices', 'AI-assisted development with human review', 'FaCode'),
  skill('JWT Authentication', 'Development Tools & Practices', 'Stateless auth', 'FaLock'),
  skill('SDLC', 'Development Tools & Practices', 'Software development lifecycle', 'FaCode'),
  skill('Data Structures & Algorithms', 'Computer Science Fundamentals', 'Arrays, trees, graphs, sorting', 'FaCode'),
  skill('Object-Oriented Programming', 'Computer Science Fundamentals', 'Design patterns and principles', 'FaCode'),
  skill('RDBMS', 'Computer Science Fundamentals', 'Database design and normalization', 'FaDatabase'),
  skill('System Design', 'Computer Science Fundamentals', 'Scalable architecture concepts', 'FaProjectDiagram'),
];
